import React from "react";
import { Param, ParamTable } from "./Paramtable";
import { MethodBadge } from "./Methodbadge";
import { EndpointHeader } from "./Endpointheader";
import { CodeBlock } from "./Codeblock";

// ─── Params ──────────────────────────────────────────────────────────

const QUERY_PARAMS: Param[] = [
  {
    name: "limit",
    type: "number",
    required: false,
    description: "Number of templates to return per page. Defaults to 10.",
  },
  {
    name: "after",
    type: "string",
    required: false,
    description: "Pagination cursor — use the `after` value from the previous response to fetch the next page.",
  },
];

// ─── cURL ─────────────────────────────────────────────────────────────

const CURL_SAMPLE = `curl 'https://apiwhat.empedance.com/api/whatsapp/company/send/search?limit=20' \\
  -H 'Authorization: Bearer <your_token>' \\
  -H 'Accept: application/json'`;

// ─── Response ─────────────────────────────────────────────────────────

const RESPONSE_SAMPLE = `{
  "status": 1,
  "data": {
    "data": [
      {
        "name": "text_header_template",
        "parameter_format": "POSITIONAL",
        "language": "en_US",
        "status": "APPROVED",
        "category": "MARKETING",
        "id": "928861599974420",
        "components": [
          { "type": "HEADER", "format": "TEXT", "text": "Text header" },
          { "type": "BODY",   "text": "This is text header template" },
          { "type": "FOOTER", "text": "Thanks" },
          {
            "type": "BUTTONS",
            "buttons": [
              { "type": "QUICK_REPLY",   "text": "I'm interested" },
              { "type": "URL",           "text": "empedance.com", "url": "https://empedance.com/" },
              { "type": "PHONE_NUMBER",  "text": "call us", "phone_number": "+919113887558" }
            ]
          }
        ]
      },
      {
        "name": "image_header_template",
        "parameter_format": "POSITIONAL",
        "language": "en_US",
        "status": "APPROVED",
        "category": "MARKETING",
        "id": "1312155494097169",
        "components": [
          {
            "type": "HEADER",
            "format": "IMAGE",
            "example": { "header_handle": ["https://scontent.whatsapp.net/..."] }
          },
          { "type": "BODY",   "text": "This is image header template." },
          { "type": "FOOTER", "text": "thanks" }
        ]
      }
    ],
    "paging": {
      "cursors": {
        "before": "<before_cursor>",
        "after":  "<after_cursor>"
      },
      "next": "https://graph.facebook.com/v22.0/<waba_id>/message_templates?limit=10&after=<after_cursor>"
    }
  },
  "error": null
}`;

// ─── Component ────────────────────────────────────────────────────────

export const GetTemplatesSection: React.FC = () => {
  return (
    <div>
      {/* Title */}
      <div className="flex items-center gap-3 mb-2">
        <MethodBadge method="GET" />
        <h1 className="text-2xl font-bold text-white">Get Templates</h1>
      </div>

      <p className="text-white/70 text-sm mt-1">
        Retrieve a paginated list of all approved WhatsApp message templates associated with your account.
      </p>

      <EndpointHeader method="GET" url="/api/whatsapp/company/send" />

      {/* Query Params */}
      <ParamTable title="Query Parameters" params={QUERY_PARAMS} />

      {/* Pagination note */}
      <div className="mt-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-600">
        <span className="font-semibold text-gray-700">Pagination:</span> The response includes a{" "}
        <code className="bg-gray-100 px-1 rounded">paging.cursors.after</code> value. Pass it as the{" "}
        <code className="bg-gray-100 px-1 rounded">after</code> query parameter to fetch the next page of templates.
        If no <code className="bg-gray-100 px-1 rounded">next</code> URL is returned, you've reached the last page.
      </div>

      {/* Request Example */}
      <CodeBlock
        title="Request Example"
        variants={[{ label: "cURL", value: CURL_SAMPLE }]}
      />

      {/* Response */}
      <CodeBlock
        title="RESPONSE 200 · OK"
        variants={[{ label: "JSON", value: RESPONSE_SAMPLE }]}
      />

      {/* Template status note */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
        <span className="font-semibold">📋 Note:</span> Only templates with a{" "}
        <code className="bg-amber-100 px-1 rounded text-xs">status: "APPROVED"</code> can be used to
        send messages. Templates in <code className="bg-amber-100 px-1 rounded text-xs">PENDING</code> or{" "}
        <code className="bg-amber-100 px-1 rounded text-xs">REJECTED</code> status are included in the
        list but cannot be dispatched.
      </div>
    </div>
  );
};